import React, {useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import {
  Dropdown,
  Toggle,
  TextInput,
  LanguageTabs,
  ButtonWithIcon
} from '@/components/atoms';

const SLICE_OPTIONS = [
  {displayText: '4', id: '4'},
  {displayText: '5', id: '5'},
  {displayText: '6', id: '6'},
  {displayText: '7', id: '7'}
];

const SLICE_COLORS = [
  'Blue',
  'Purple',
  'Pink',
  'Red',
  'Orange',
  'Yellow',
  'Green'
];

const GameSettingsSection = ({
  languages,
  gameSettingsActiveTabIndex,
  setGameSettingsActiveTabIndex,
  gameSettingsCurrentLangData,
  updateGameSettingsField,
  slices,
  setSlices,
  equalProbability,
  setEqualProbability,
  equalPoints,
  setEqualPoints,
  sliceProbabilities,
  setSliceProbabilities,
  slicePoints,
  setSlicePoints,
  totalProbability,
  setShowGuidelines
}) => {
  const validSliceValues = [4, 5, 6, 7];
  const isSlicesValid = validSliceValues.includes(slices);

  const slicesValue = useMemo(
    () =>
      isSlicesValid ? [{displayText: String(slices), id: String(slices)}] : [],
    [slices, isSlicesValid]
  );

  const handleSlicesChange = useCallback(
    (val) => {
      const selected = Array.isArray(val) ? val[0] : val;
      if (!selected) {
        setSlices(null);
        return;
      }
      const newSlices = Number(selected.id || selected.displayText);
      setSlices(newSlices);
    },
    [setSlices]
  );

  const handleProbabilityChange = useCallback(
    (index, value) => {
      if (value.startsWith('-')) return;
      const cleaned = value.replace(/[^0-9.]/g, '');
      setSliceProbabilities((prev) => {
        const updated = [...prev];
        updated[index] = cleaned;
        return updated;
      });
    },
    [setSliceProbabilities]
  );

  const handlePointsChange = useCallback(
    (index, value) => {
      const cleaned = value.replace(/\D/g, '');
      setSlicePoints((prev) => {
        const updated = [...prev];
        updated[index] = cleaned;
        return updated;
      });
    },
    [setSlicePoints]
  );

  const blockPointsKeyDown = useCallback((e) => {
    if (e.key.length === 1 && !/\d/.test(e.key)) {
      e.preventDefault();
    }
  }, []);

  const blockPointsPaste = useCallback((e) => {
    const pasted = e.clipboardData?.getData('text');
    if (pasted && !/^\d*$/.test(pasted)) {
      e.preventDefault();
    }
  }, []);

  const blockProbabilityKeyDown = useCallback((e) => {
    if (e.key === '-' || (e.key.length === 1 && !/[\d.]/.test(e.key))) {
      e.preventDefault();
    }
  }, []);

  const blockProbabilityPaste = useCallback((e) => {
    const pasted = e.clipboardData?.getData('text');
    if (pasted && !/^[\d.]*$/.test(pasted)) {
      e.preventDefault();
    }
  }, []);

  const allSliceProbability = slices
    ? `${Number((100 / slices).toFixed(1))}%`
    : '';

  return (
    <div className='flex max-w-[1300px] flex-col gap-4'>
      <div className='flex items-start gap-6'>
        <Dropdown
          label='Slices*'
          value={slicesValue}
          updateValue={handleSlicesChange}
          isJsonWrap={false}
          childList={SLICE_OPTIONS}
          className='dropdown-width text-sm'
          isDark={true}
          placeholder='Select Slices'
          isClearable={false}
          selection='single'
          size='medium'
        />
        <div className='mt-7'>
          <ButtonWithIcon
            id='view-guideline'
            iconLeading='open_in_full'
            handleButton={() => setShowGuidelines(true)}
            uiSchema={{
              title: 'View Guideline',
              variant: 'tertiary',
              size: 'small',
              isDark: true
            }}
          />
        </div>
      </div>

      <div className='flex flex-wrap items-center gap-6'>
        <div style={{width: 275}}>
          {' '}
          <Toggle
            value={equalProbability}
            updateValue={() => setEqualProbability((prev) => !prev)}
            uiSchema={{
              isDark: true,
              size: 'medium',
              label: 'Equal Probability per Slice'
            }}
            disabled={false}
            id='equal-probability'
          />
        </div>
        {equalProbability && (
          <div className='flex flex-col gap-1'>
            <span className='text-xs text-[#DADADA]'>Probability</span>
            <div style={{width: 90}}>
              <TextInput
                value={allSliceProbability}
                onChange={() => {}}
                isDark
                disabled
                id='all-slice-probability'
              />
            </div>
          </div>
        )}
        {!equalProbability && (
          <div className='flex flex-wrap items-start gap-3'>
            {Array.from({length: slices}, (_, i) => (
              <div key={i} className='flex flex-col gap-1'>
                <span className='text-xs text-[#DADADA]'>
                  Slice {i + 1} - {SLICE_COLORS[i]}
                </span>
                <div style={{width: 90}}>
                  <TextInput
                    value={sliceProbabilities[i] || ''}
                    onChange={(val) => handleProbabilityChange(i, val)}
                    isDark
                    placeholderText='%'
                    id={`slice-probability-${i}`}
                    handleKeyDown={blockProbabilityKeyDown}
                    onPaste={blockProbabilityPaste}
                  />
                </div>
              </div>
            ))}
            {totalProbability !== 100 && (
              <span className='mt-5 text-sm text-[var(--color-feedback-error-high-contrast)]'>
                Total probability must equal 100%.
              </span>
            )}
          </div>
        )}
      </div>

      <div className='flex flex-wrap items-center gap-6'>
        <div style={{width: 275}}>
          <Toggle
            value={equalPoints}
            updateValue={() => setEqualPoints((prev) => !prev)}
            uiSchema={{
              isDark: true,
              size: 'medium',
              label: 'Equal Points per Slice'
            }}
            disabled={false}
            id='equal-points'
          />
        </div>
        {equalPoints && (
          <div className='flex flex-col gap-1'>
            <span className='text-xs text-[#DADADA]'>All Slices</span>
            <div style={{width: 90}}>
              <TextInput
                value={slicePoints[0] || ''}
                onChange={(val) => {
                  const cleaned = val.replace(/\D/g, '');
                  setSlicePoints(Array.from({length: slices}, () => cleaned));
                }}
                isDark
                id='all-slice-points'
                handleKeyDown={blockPointsKeyDown}
                onPaste={blockPointsPaste}
              />
            </div>
          </div>
        )}
        {!equalPoints && (
          <div className='flex flex-wrap items-start gap-3'>
            {Array.from({length: slices}, (_, i) => (
              <div key={i} className='flex flex-col gap-1'>
                <span className='text-xs text-[#DADADA]'>
                  Slice {i + 1} - {SLICE_COLORS[i]}
                </span>
                <div style={{width: 90}}>
                  <TextInput
                    value={slicePoints[i] || ''}
                    onChange={(val) => handlePointsChange(i, val)}
                    isDark
                    placeholderText='Points'
                    id={`slice-points-${i}`}
                    handleKeyDown={blockPointsKeyDown}
                    onPaste={blockPointsPaste}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className='flex w-full max-w-[640px] flex-col gap-2'>
        <LanguageTabs
          languages={languages}
          activeTabIndex={gameSettingsActiveTabIndex}
          setActiveTabIndex={(index) => setGameSettingsActiveTabIndex(index)}
          testIdPrefix='game-settings-language-tab'
          dividerClassName='-mt-2 mb-0 border-0 border-b'
        />
        <div className='flex gap-4'>
          <div className='flex-1'>
            <TextInput
              value={gameSettingsCurrentLangData.pointsUnitOfMeasure}
              onChange={(val) =>
                updateGameSettingsField('pointsUnitOfMeasure', val)
              }
              label='Points (Unit of Measure)*'
              isDark
              maxLength={55}
              placeholderText='Points'
              id='points-unit-of-measure'
            />
          </div>
          <div className='flex-1'>
            <TextInput
              value={gameSettingsCurrentLangData.noPointsAwarded}
              onChange={(val) =>
                updateGameSettingsField('noPointsAwarded', val)
              }
              label='No Points Awarded*'
              isDark
              maxLength={55}
              placeholderText='Try Again'
              id='no-points-awarded'
            />
          </div>
        </div>
      </div>
    </div>
  );
};

GameSettingsSection.propTypes = {
  languages: PropTypes.array.isRequired,
  gameSettingsActiveTabIndex: PropTypes.number.isRequired,
  setGameSettingsActiveTabIndex: PropTypes.func.isRequired,
  gameSettingsCurrentLangData: PropTypes.object.isRequired,
  updateGameSettingsField: PropTypes.func.isRequired,
  slices: PropTypes.number.isRequired,
  setSlices: PropTypes.func.isRequired,
  equalProbability: PropTypes.bool.isRequired,
  setEqualProbability: PropTypes.func.isRequired,
  equalPoints: PropTypes.bool.isRequired,
  setEqualPoints: PropTypes.func.isRequired,
  sliceProbabilities: PropTypes.array.isRequired,
  setSliceProbabilities: PropTypes.func.isRequired,
  slicePoints: PropTypes.array.isRequired,
  setSlicePoints: PropTypes.func.isRequired,
  totalProbability: PropTypes.number.isRequired,
  setShowGuidelines: PropTypes.func.isRequired
};

export default React.memo(GameSettingsSection);
